import { useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { CalendarDays, Database, FlaskConical, GraduationCap, Heart, Pencil, Trash2, Users, UsersRound } from 'lucide-react';
import { DashboardShell } from '../layout/DashboardShell';
import { UserMenu } from '../components/UserMenu';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { useAnalysis } from '../context/AnalysisContext';
import { useEvents } from '../context/EventsContext';
import { useRealProfiles } from '../hooks/useRealProfiles';
import { filterProfiles, eventDateRange, indexOperatorsByRoomId, matchesViewMode } from '../utils/profileFilter';
import {
  analysisTypeLabel,
  RESEARCH_OBJECTIVE_LABELS,
  ANALYSIS_TYPE_LABELS,
  ANALYSIS_METHOD_BY_TYPE,
  MIN_EVENTS_FOR_ANALYSIS,
} from '../types/analysis';
import { DashPanel } from '../components/DashPanel';
import { DataCardHeader } from '../components/CardExportButton';
import { MetricStat } from '../components/MetricStat';
import { RankedList } from '../components/RankedList';
import { GroupedColumnChart } from '../components/charts/Heatmap';
import { HourClockChart } from '../components/charts/Heatmap';
import { ColumnChart, BarChartHorizontal } from '../components/charts/DashCharts';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { exportCardCsv } from '../utils/csvExport';
import { computeRealCommunicationTotals } from '../utils/realCommunicationAnalytics';
import { computeRealRoutinesAnalytics } from '../utils/realRoutinesAnalytics';
import { computeRealGamesAnalytics, formatResponseTime } from '../utils/realGamesAnalytics';
import { computeRealAIAnalytics } from '../utils/realAIAnalytics';
import { computeRealMusicAnalytics } from '../utils/realMusicAnalytics';
import { computeRealGeneralAnalytics } from '../utils/realGeneralAnalytics';
import { RoundHighlightCard } from '../modules/GamesModule';
import type { RoutineDetailRow, SubRoutineDetailRow } from '../types/routines';
import type { RoundStats } from '../types/games';
import { cn } from '@/lib/utils';

type ModuleTab = 'comunicacao' | 'rotinas' | 'jogos' | 'ia' | 'musica';

// ── sub-components ────────────────────────────────────────────────────

function AlarmBadge({ hasAlarm }: { hasAlarm: boolean }) {
  return (
    <span className={cn('text-[11px] font-medium px-1.5 py-0.5 rounded-full',
      hasAlarm
        ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400'
        : 'bg-muted text-muted-foreground')}>
      {hasAlarm ? 'Com alarme' : 'Sem alarme'}
    </span>
  );
}

function RoutineDetailTable({ title, rows, onExport }: { title: string; rows: RoutineDetailRow[]; onExport?: () => void }) {
  return (
    <DashPanel padding="sm">
      <DataCardHeader title={title} onExport={onExport} />
      <div className="max-h-[280px] overflow-y-auto pr-1 scroll-styled">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] text-muted-foreground">
              <th className="py-1.5 font-medium">Rotina</th>
              <th className="py-1.5 font-medium">Horário</th>
              <th className="py-1.5 font-medium">Alarme</th>
              <th className="py-1.5 font-medium">Dia(s)</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.name} className="border-t border-border">
                <td className="py-2 text-foreground">{row.name}</td>
                <td className="py-2 text-muted-foreground tabular-nums">{row.typicalHour}</td>
                <td className="py-2"><AlarmBadge hasAlarm={row.hasAlarm} /></td>
                <td className="py-2 text-muted-foreground">{row.weekdays}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </DashPanel>
  );
}

function SubRoutineDetailTable({ title, rows, onExport }: { title: string; rows: SubRoutineDetailRow[]; onExport?: () => void }) {
  return (
    <DashPanel padding="sm">
      <DataCardHeader title={title} onExport={onExport} />
      <div className="max-h-[280px] overflow-y-auto pr-1 scroll-styled">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] text-muted-foreground">
              <th className="py-1.5 font-medium">Subrotina</th>
              <th className="py-1.5 font-medium">Rotina</th>
              <th className="py-1.5 font-medium">Horário</th>
              <th className="py-1.5 font-medium">Alarme</th>
              <th className="py-1.5 font-medium">Dia(s)</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr key={`${row.name}-${idx}`} className="border-t border-border">
                <td className="py-2 text-foreground">{row.name}</td>
                <td className="py-2 text-muted-foreground">{row.routineName}</td>
                <td className="py-2 text-muted-foreground tabular-nums">{row.hour}</td>
                <td className="py-2"><AlarmBadge hasAlarm={row.hasAlarm} /></td>
                <td className="py-2 text-muted-foreground">{row.weekdays}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </DashPanel>
  );
}

function RoundsTable({ title, rounds, onExport }: { title: string; rounds: RoundStats[]; onExport?: () => void }) {
  return (
    <DashPanel padding="sm">
      <DataCardHeader title={title} onExport={onExport} />
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[11px] text-muted-foreground">
            <th className="py-1.5 font-medium w-8">#</th>
            <th className="py-1.5 font-medium">Comando</th>
            <th className="py-1.5 font-medium text-right">Acertos</th>
            <th className="py-1.5 font-medium text-right">Erros</th>
            <th className="py-1.5 font-medium text-right">Acerto</th>
          </tr>
        </thead>
        <tbody>
          {rounds.map((r) => (
            <tr key={r.roundId} className="border-t border-border">
              <td className="py-2 text-muted-foreground tabular-nums">{r.order}</td>
              <td className="py-2 text-foreground">{r.prompt}</td>
              <td className="py-2 text-right tabular-nums">{r.correct}</td>
              <td className="py-2 text-right tabular-nums">{r.incorrect}</td>
              <td className="py-2 text-right font-medium tabular-nums">{r.accuracyPct}%</td>
            </tr>
          ))}
        </tbody>
      </table>
    </DashPanel>
  );
}

// ── main component ────────────────────────────────────────────────────

export default function AnalysisDetail() {
  const { analysisId } = useParams<{ analysisId: string }>();
  const { getAnalysisById, deleteAnalysis } = useAnalysis();
  const { events } = useEvents();
  const { profiles: realProfiles, operators } = useRealProfiles();
  const navigate = useNavigate();
  const analysis = analysisId ? getAnalysisById(analysisId) : undefined;
  const [activeTab, setActiveTab] = useState<ModuleTab>('comunicacao');
  const [selectedGameId, setSelectedGameId] = useState<string | null>(null);

  // ── useMemo ANTES do early return (regra dos hooks React) ───────────
  const isGeral = analysis?.types.includes('geral') ?? false;
  const linked = useMemo(
    () => (analysis ? events.filter((e) => analysis.eventIds.includes(e.id)) : []),
    [analysis, events]
  );
  const chartEvents = isGeral ? events : linked;

  // Análise científica só considera alunos gerenciados de verdade — uso
  // individual (capacitação de professor/pai) nunca entra nos totais
  // (ver conectar.md, tarefa S4).
  const analyzedProfiles = useMemo(() => {
    if (!analysis) return [];
    const seen = new Set<string>();
    const result: typeof realProfiles = [];
    for (const ev of chartEvents) {
      const range = eventDateRange(Boolean(ev.noPeriod), ev.startDate, ev.endDate);
      const evProfiles = filterProfiles(realProfiles, 'managed', { dateRange: range, operators });
      for (const p of evProfiles) {
        if (!seen.has(p.id)) { seen.add(p.id); result.push(p); }
      }
    }
    return result;
  }, [analysis, chartEvents, realProfiles, operators]);

  // Só pro card de topo (quebra por papel + total geral) — os gráficos das
  // abaixo continuam só com `analyzedProfiles` (aluno), sem mudar a regra
  // acima. Mesma união de período por evento, só sem restringir o papel.
  const allRoleProfiles = useMemo(() => {
    if (!analysis) return [];
    const seen = new Set<string>();
    const result: typeof realProfiles = [];
    for (const ev of chartEvents) {
      const range = eventDateRange(Boolean(ev.noPeriod), ev.startDate, ev.endDate);
      const evProfiles = filterProfiles(realProfiles, 'todos', { dateRange: range, operators });
      for (const p of evProfiles) {
        if (!seen.has(p.id)) { seen.add(p.id); result.push(p); }
      }
    }
    return result;
  }, [analysis, chartEvents, realProfiles, operators]);

  const roleCounts = useMemo(() => {
    const operatorsByRoomId = indexOperatorsByRoomId(operators);
    let managed = 0;
    let professor = 0;
    let paiMae = 0;
    for (const p of allRoleProfiles) {
      if (matchesViewMode(p, 'managed', operatorsByRoomId)) managed++;
      else if (matchesViewMode(p, 'professor', operatorsByRoomId)) professor++;
      else if (matchesViewMode(p, 'pai_mae', operatorsByRoomId)) paiMae++;
    }
    return {
      managed,
      professor,
      paiMae,
      total: allRoleProfiles.length,
      unclassified: allRoleProfiles.length - (managed + professor + paiMae),
    };
  }, [allRoleProfiles, operators]);

  const commData    = useMemo(() => computeRealCommunicationTotals(analyzedProfiles), [analyzedProfiles]);
  const routineData = useMemo(() => computeRealRoutinesAnalytics(analyzedProfiles), [analyzedProfiles]);
  const gamesData   = useMemo(() => computeRealGamesAnalytics(analyzedProfiles), [analyzedProfiles]);
  const aiData      = useMemo(() => computeRealAIAnalytics(analyzedProfiles), [analyzedProfiles]);
  const musicData   = useMemo(() => computeRealMusicAnalytics(analyzedProfiles), [analyzedProfiles]);
  const generalData = useMemo(() => computeRealGeneralAnalytics(analyzedProfiles), [analyzedProfiles]);
  // "Registros totais" usa todos os perfis dos eventos (professor, pai/mãe e sem
  // papel definido), igual ao painel de Eventos. Os gráficos continuam só com alunos.
  const allRecordsData = useMemo(() => computeRealGeneralAnalytics(allRoleProfiles), [allRoleProfiles]);
  // ─────────────────────────────────────────────────────────────────────

  if (!analysis) return <Navigate to="/dashboard/analysis" replace />;

  const handleDelete = () => {
    deleteAnalysis(analysis.id);
    navigate('/dashboard/analysis', { replace: true });
  };

  const created = format(parseISO(analysis.createdAt), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
  const totalRecords = allRecordsData.totalRecords;

  const notEnoughEvents = chartEvents.length < MIN_EVENTS_FOR_ANALYSIS;

  return (
    <DashboardShell mode="analysis">
      <header className="shrink-0 px-6 py-4 flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <FlaskConical className="h-4 w-4 text-primary" />
            <p className="text-xs text-primary font-medium">{analysisTypeLabel(analysis)}</p>
          </div>
          <h1 className="text-base font-semibold text-foreground mt-0.5">{analysis.title}</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={() => navigate(`/dashboard/analysis/${analysis.id}/edit`)}
          >
            <Pencil className="h-3.5 w-3.5" />
            Editar
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1.5 text-destructive hover:text-destructive">
                <Trash2 className="h-3.5 w-3.5" />
                Excluir
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Excluir análise</AlertDialogTitle>
                <AlertDialogDescription>
                  Tem certeza que deseja excluir "{analysis.title}"? Essa ação não pode ser desfeita.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  onClick={handleDelete}
                >
                  Excluir
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <UserMenu />
        </div>
      </header>

      <div className="flex-1 overflow-y-auto p-6 space-y-6 bg-blue-100 dark:bg-background border-t border-l border-border rounded-tl-2xl">

        {/* ── Hero — mesmo cartão claro usado no resto do dashboard (DashPanel / EventCoverBanner) ─── */}
        <DashPanel padding="md">
          <div className="flex items-center gap-2 mb-5">
            <FlaskConical className="h-4 w-4 text-primary" />
            <span className="text-[11px] font-semibold tracking-widest uppercase text-primary">
              Análise cruzada — {created}
            </span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-6">
            <MetricStat
              icon={CalendarDays}
              label={isGeral ? 'Eventos (todos)' : 'Eventos (selecionados)'}
              value={chartEvents.length}
            />
            <MetricStat icon={Users} label="Professores" value={roleCounts.professor} />
            <MetricStat icon={GraduationCap} label="Alunos" value={roleCounts.managed} />
            <MetricStat icon={Heart} label="Pai/Mãe" value={roleCounts.paiMae} />
            <MetricStat icon={UsersRound} label="Total" value={roleCounts.total} />
            <MetricStat icon={Database} label="Registros totais" value={totalRecords} />
          </div>
          {roleCounts.unclassified > 0 && (
            <p className="mt-2 text-[11px] text-muted-foreground">
              O total inclui {roleCounts.unclassified} perfil{roleCounts.unclassified === 1 ? '' : 's'} sem papel definido (não entra em nenhuma das três contagens acima).
            </p>
          )}
          {(analysis.researchObjectives.length > 0 || analysis.types.length > 0) && (
            <div className="mt-5 pt-4 border-t border-border flex flex-wrap gap-2">
              {analysis.researchObjectives.map((o) => (
                <span key={o} className="text-[11px] font-medium bg-muted text-muted-foreground px-2 py-0.5 rounded-full">
                  {RESEARCH_OBJECTIVE_LABELS[o]}
                </span>
              ))}
              {analysis.types.map((t) => (
                <span key={t} className="text-[11px] font-medium bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                  {ANALYSIS_TYPE_LABELS[t]}
                </span>
              ))}
            </div>
          )}
        </DashPanel>

        {notEnoughEvents ? (
          <DashPanel padding="sm" className="flex flex-col items-center gap-3 py-6">
            <p className="text-sm text-muted-foreground text-center">
              {chartEvents.length === 0
                ? 'Nenhum evento vinculado.'
                : `Esta análise tem ${chartEvents.length} evento vinculado.`}{' '}
              Para cruzar dados são necessários pelo menos {MIN_EVENTS_FOR_ANALYSIS} eventos.
            </p>
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() => navigate(`/dashboard/analysis/${analysis.id}/edit`)}
            >
              <Pencil className="h-3.5 w-3.5" />
              Vincular eventos
            </Button>
          </DashPanel>
        ) : (
          <>
            {/* ── Card principal: ranking de funcionalidades (dados reais) ─── */}
            <DashPanel padding="sm">
              <DataCardHeader
                title="Funcionalidades mais usadas — dados reais"
                onExport={() =>
                  exportCardCsv('analise-funcionalidades', ['Funcionalidade', 'Registros', '%'],
                    generalData.featureUsage.map((r) => [r.name, r.value, r.pct]))
                }
              />
              <div className="mt-3 space-y-3">
                {['#4F7CFF','#22C55E','#F59E0B','#EC4899','#8B5CF6'].map((color, idx) => {
                  const row = generalData.featureUsage[idx];
                  if (!row) return null;
                  return (
                  <div key={row.name}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <div className="flex items-center gap-2">
                        <span className="text-[11px] text-muted-foreground w-4 text-right font-medium">{idx + 1}.</span>
                        <span className="font-medium text-foreground">{row.name}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-muted-foreground tabular-nums">{row.value.toLocaleString('pt-BR')} registros</span>
                        <span className="text-xs font-semibold tabular-nums w-8 text-right" style={{ color }}>{row.pct}%</span>
                      </div>
                    </div>
                    <div className="h-2 rounded-full bg-muted overflow-hidden">
                      <div className="h-full rounded-full transition-all" style={{ width: `${row.pct}%`, background: color }} />
                    </div>
                  </div>
                  );
                })}
              </div>
            </DashPanel>

            {/* ── Tabs dos módulos ─── */}
            <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as ModuleTab)}>
              <TabsList>
                <TabsTrigger value="comunicacao" className="text-xs">Comunicação</TabsTrigger>
                <TabsTrigger value="rotinas" className="text-xs">Rotinas</TabsTrigger>
                <TabsTrigger value="jogos" className="text-xs">Jogos</TabsTrigger>
                <TabsTrigger value="ia" className="text-xs">Desenho com IA</TabsTrigger>
                <TabsTrigger value="musica" className="text-xs">Música</TabsTrigger>
              </TabsList>
            </Tabs>

                        {/* ══ COMUNICAÇÃO ══ */}
            {activeTab === 'comunicacao' && (
              <div className="space-y-6">
                <div className="pb-4 border-b border-border flex flex-wrap gap-8">
                  <div>
                    <p className="text-xs text-muted-foreground">Categorias disponíveis</p>
                    <p className="text-xl font-semibold tabular-nums">{commData.totalCategoriesAvailable}</p>
                    <p className="text-[10px] text-muted-foreground">10 nativas do app + {commData.totalCategoriesAvailable - 10} criadas pelos participantes</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Subcategorias disponíveis</p>
                    <p className="text-xl font-semibold tabular-nums">{commData.totalSubcategoriesAvailable}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Categorias exploradas</p>
                    <p className="text-xl font-semibold tabular-nums">{commData.categoriesUsed} / {commData.totalCategoriesAvailable}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Cliques totais</p>
                    <p className="text-xl font-semibold tabular-nums">{commData.totalClicks.toLocaleString('pt-BR')}</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <RankedList title="Categorias clicadas" rows={commData.topCategories.map(r => ({ label: r.name, value: r.value.toLocaleString('pt-BR') }))} onExport={() => exportCardCsv('analise-comm-categorias', ['Categoria', 'Cliques'], commData.topCategories.map(r => [r.name, r.value]))} />
                  <RankedList title="Subcategorias clicadas" rows={commData.topSubcategories.map(r => ({ label: r.name, value: r.value.toLocaleString('pt-BR') }))} onExport={() => exportCardCsv('analise-comm-subcategorias', ['Subcategoria', 'Cliques'], commData.topSubcategories.map(r => [r.name, r.value]))} />
                </div>
                {commData.userCreatedCategories.length > 0 && (
                  <RankedList title={`Categorias criadas pelos participantes (${commData.userCreatedCategories.length})`} rows={commData.userCreatedCategories.map(n => ({ label: n, value: '---' }))} />
                )}
              </div>
            )}

            {/* ══ ROTINAS ══ */}
            {activeTab === 'rotinas' && (
              <div className="space-y-6">
                <div className="pb-4 border-b border-border">
                  <p className="text-xs text-muted-foreground">Rotinas com subrotinas criadas</p>
                  <p className="text-xl font-semibold tabular-nums">{routineData.routinesUsed}</p>
                </div>
                <RankedList title="Rotinas mais usadas" rows={routineData.rankedRoutines.map(r => ({ label: r.name, value: r.value.toLocaleString('pt-BR') }))} onExport={() => exportCardCsv('analise-rotinas-ranking', ['Rotina', 'Total'], routineData.rankedRoutines.map(r => [r.name, r.value]))} />
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <RoutineDetailTable title="Rotinas" rows={routineData.routineDetails} onExport={() => exportCardCsv('analise-rotinas-det', ['Rotina', 'Horario', 'Alarme', 'Dias'], routineData.routineDetails.map(r => [r.name, r.typicalHour, r.hasAlarm ? 'Sim' : 'Nao', r.weekdays]))} />
                  <SubRoutineDetailTable title="Subrotinas" rows={routineData.subRoutineDetails} onExport={() => exportCardCsv('analise-subrotinas-det', ['Subrotina', 'Rotina', 'Horario', 'Alarme', 'Dias'], routineData.subRoutineDetails.map(r => [r.name, r.routineName, r.hour, r.hasAlarm ? 'Sim' : 'Nao', r.weekdays]))} />
                </div>
                <ColumnChart title="Subrotinas por dia da semana configurado" data={routineData.byWeekday.map(w => ({ day: w.label, count: w.count }))} nameKey="day" valueKey="count" onExport={() => exportCardCsv('analise-rotinas-dia', ['Dia', 'Subrotinas'], routineData.byWeekday.map(w => [w.label, w.count]))} />
                <HourClockChart title="Horários configurados (por hora do dia)" data={routineData.byHourBlock} onExport={() => exportCardCsv('analise-rotinas-hora', ['Horario', 'Subrotinas'], routineData.byHourBlock.map(b => [b.label, b.count]))} />
                {routineData.alarmAdherence.length > 0 && <BarChartHorizontal title="Rotinas com mais alarmes configurados" data={routineData.alarmAdherence.map(r => ({ name: r.routineName, count: r.alarmCount }))} nameKey="name" valueKey="count" onExport={() => exportCardCsv('analise-rotinas-alarme', ['Rotina', 'Alarmes'], routineData.alarmAdherence.map(r => [r.routineName, r.alarmCount]))} />}
                {routineData.routineSeries.length > 0 && <GroupedColumnChart title="Dia da semana x rotina" data={routineData.weekdayByRoutine} nameKey="day" series={routineData.routineSeries} onExport={() => exportCardCsv('analise-rotinas-dia-det', ['Dia'], routineData.weekdayByRoutine.map(row => [row.day as string]))} />}
              </div>
            )}

            {/* ══ JOGOS ══ */}
            {activeTab === 'jogos' && (() => {
              const activeGameId = selectedGameId ?? gamesData.overview[0]?.gameId ?? null;
              const selectedGame = activeGameId ? gamesData.byGame[activeGameId] : undefined;
              return (
                <div className="space-y-6">
                  {gamesData.overview.length > 0 ? (
                    <>
                      <BarChartHorizontal title="Taxa de acerto por jogo (%)" data={gamesData.overview.map(g => ({ name: g.name, accuracy: g.accuracyPct }))} nameKey="name" valueKey="accuracy" onExport={() => exportCardCsv('analise-jogos-acerto', ['Jogo', 'Acerto (%)'], gamesData.overview.map(g => [g.name, g.accuracyPct]))} />
                      <Tabs value={activeGameId ?? ''} onValueChange={setSelectedGameId}>
                        <TabsList>{gamesData.overview.map(g => <TabsTrigger key={g.gameId} value={g.gameId} className="text-xs">{g.name}</TabsTrigger>)}</TabsList>
                      </Tabs>
                      {selectedGame && (
                        <>
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                            <DashPanel padding="sm"><MetricStat label="Acertos" value={selectedGame.correct} /></DashPanel>
                            <DashPanel padding="sm"><MetricStat label="Erros" value={selectedGame.incorrect} /></DashPanel>
                            <DashPanel padding="sm"><MetricStat label="Taxa de acerto" value={selectedGame.accuracyPct} suffix="%" /></DashPanel>
                            <DashPanel padding="sm"><MetricStat label="Tempo médio de resposta" value={formatResponseTime(selectedGame.avgResponseTimeMs)} /></DashPanel>
                          </div>
                          <RoundsTable title={`Rodadas — ${selectedGame.name}`} rounds={selectedGame.rounds} onExport={() => exportCardCsv(`analise-jogos-rodadas`, ['Rodada', 'Comando', 'Acertos', 'Erros', 'Acerto (%)'], selectedGame.rounds.map(r => [r.order, r.prompt, r.correct, r.incorrect, r.accuracyPct]))} />
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            <RoundHighlightCard label="Rodada mais difícil (maior taxa de erro)" round={selectedGame.hardestRound} />
                            <RoundHighlightCard label="Rodada mais fácil (maior taxa de acerto)" round={selectedGame.easiestRound} />
                          </div>
                        </>
                      )}
                    </>
                  ) : (
                    <p className="text-sm text-muted-foreground">Nenhum dado de jogo nos perfis selecionados.</p>
                  )}
                </div>
              );
            })()}

            {/* ══ DESENHO COM IA ══ */}
            {activeTab === 'ia' && (
              <div className="space-y-6">
                <div className="pb-4 border-b border-border flex flex-wrap gap-8">
                  <div>
                    <p className="text-xs text-muted-foreground">Desenhos reconhecidos</p>
                    <p className="text-xl font-semibold tabular-nums">{aiData.totalDrawings.toLocaleString('pt-BR')}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Índice de confiança do reconhecimento</p>
                    <p className="text-xl font-semibold tabular-nums">{aiData.confidenceIndex}%</p>
                    <p className="text-[10px] text-muted-foreground">% dos desenhos reconhecidos como correspondência direta</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Letras</p>
                    <p className="text-xl font-semibold tabular-nums">{aiData.typeDistribution.letters} ({aiData.typeDistribution.lettersPct}%)</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Números</p>
                    <p className="text-xl font-semibold tabular-nums">{aiData.typeDistribution.numbers} ({aiData.typeDistribution.numbersPct}%)</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <DashPanel padding="sm"><MetricStat label='Reconhecimento direto ("Você desenhou")' value={aiData.outcomeBreakdown.direct} /></DashPanel>
                  <DashPanel padding="sm"><MetricStat label='Reconhecimento sugerido ("Isso parece")' value={aiData.outcomeBreakdown.suggestive} /></DashPanel>
                  <DashPanel padding="sm"><MetricStat label="Total de desenhos reconhecidos" value={aiData.outcomeBreakdown.total} /></DashPanel>
                </div>
                <RankedList title="Caracteres reconhecidos" rows={aiData.recognizedCharacters.map(r => ({ label: r.character, value: r.count.toLocaleString('pt-BR') }))} onExport={() => exportCardCsv('analise-ia-caracteres', ['Caractere', 'Qtd'], aiData.recognizedCharacters.map(r => [r.character, r.count]))} />
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <RankedList title="Letras mais praticadas" rows={aiData.topLetters.map(r => ({ label: r.character, value: r.count.toLocaleString('pt-BR') }))} onExport={() => exportCardCsv('analise-ia-letras', ['Letra', 'Qtd'], aiData.topLetters.map(r => [r.character, r.count]))} />
                  <RankedList title="Numeros mais praticados" rows={aiData.topNumbers.map(r => ({ label: r.character, value: r.count.toLocaleString('pt-BR') }))} onExport={() => exportCardCsv('analise-ia-numeros', ['Numero', 'Qtd'], aiData.topNumbers.map(r => [r.character, r.count]))} />
                </div>
                <RankedList
                  title="Desenhos com melhor acurácia do modelo"
                  emptyLabel="Sem registros com % de confiança nos perfis selecionados"
                  rows={aiData.bestAccuracy.map(r => ({
                    label: r.character,
                    value: `${r.avgConfidencePct}% · ${r.totalClassifications.toLocaleString('pt-BR')} classificações`,
                    pct: r.avgConfidencePct,
                  }))}
                  onExport={() => exportCardCsv(
                    'analise-ia-acuracia',
                    ['Caractere', 'Acurácia média (%)', 'Classificações'],
                    aiData.bestAccuracy.map(r => [r.character, r.avgConfidencePct, r.totalClassifications])
                  )}
                />
              </div>
            )}

            {/* ══ MUSICA ══ */}
            {activeTab === 'musica' && (
              <div className="space-y-6">
                <div className="pb-4 border-b border-border flex flex-wrap gap-8">
                  <div>
                    <p className="text-xs text-muted-foreground">Reproduções totais</p>
                    <p className="text-xl font-semibold tabular-nums">{musicData.totalPlays.toLocaleString('pt-BR')}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Música mais tocada</p>
                    <p className="text-xl font-semibold tabular-nums">{musicData.mostPlayedSong?.name ?? '---'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Gênero líder</p>
                    <p className="text-xl font-semibold tabular-nums">{musicData.topGenres[0]?.name ?? '---'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Músicas distintas / Gêneros distintos</p>
                    <p className="text-xl font-semibold tabular-nums">{musicData.songsUsed} / {musicData.genresUsed}</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <RankedList title="Músicas mais reproduzidas" rows={musicData.topSongs.map(r => ({ label: r.name, value: r.value.toLocaleString('pt-BR') }))} onExport={() => exportCardCsv('analise-musica-songs', ['Musica', 'Rep.'], musicData.topSongs.map(r => [r.name, r.value]))} />
                  <RankedList title="Gêneros mais reproduzidos" rows={musicData.topGenres.map(r => ({ label: r.name, value: r.value.toLocaleString('pt-BR') }))} onExport={() => exportCardCsv('analise-musica-generos', ['Genero', 'Rep.'], musicData.topGenres.map(r => [r.name, r.value]))} />
                </div>
                <RankedList title="Participação por gênero (% das reproduções)" rows={musicData.genreShare.map(r => ({ label: r.genre, value: `${r.pct}%` }))} onExport={() => exportCardCsv('analise-musica-share', ['Genero', 'Rep.', '%'], musicData.genreShare.map(r => [r.genre, r.value, r.pct]))} />
              </div>
            )}
          </>
        )}

        {/* ── Metadados ─── */}
        <DashPanel padding="sm">
          <h3 className="text-sm font-medium text-foreground mb-3">Informações da análise</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Criado em</span>
              <span className="font-medium">{created}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Eventos vinculados</span>
              <span className="font-medium">{linked.length > 0 ? linked.length : isGeral ? `Todos (${events.length})` : '0'}</span>
            </div>
            {analysis.types.map((t) => (
              <div key={t} className="pt-1 border-t border-border">
                <p className="text-xs text-muted-foreground">{ANALYSIS_TYPE_LABELS[t]}</p>
                <p className="text-xs leading-relaxed mt-0.5">{ANALYSIS_METHOD_BY_TYPE[t]}</p>
              </div>
            ))}
          </div>
        </DashPanel>
      </div>
    </DashboardShell>
  );
}
